import { ConfigurableToolInputSchemas } from "@app/lib/actions/mcp_internal_actions/input_schemas";
import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import {
  PodTasksAcceptProposalsInputSchema,
  PodTasksCreateTasksInputSchema,
  PodTasksProposeTasksInputSchema,
  PodTasksUpdateTasksInputSchema,
} from "@app/lib/api/actions/servers/pod_tasks/types";
import { INTERNAL_MIME_TYPES } from "@memoair/client";
import { z } from "zod";

export const POD_TASKS_SERVER_NAME = "pod_tasks" as const;
export const CREATE_TASKS_TOOL_NAME = "create_tasks" as const;
export const UPDATE_TASKS_TOOL_NAME = "update_tasks" as const;
export const START_TASK_AGENT_TOOL_NAME = "start_task_agent" as const;
export const PROPOSE_TASKS_TOOL_NAME = "propose_tasks" as const;
export const ACCEPT_PROPOSALS_TOOL_NAME = "accept_proposals" as const;

export const POD_TASKS_TOOLS_METADATA = [
  {
    name: "list_tasks",
    description:
      "List tasks in the Pod. " +
      "Defaults to the current user's tasks (assigneeFilter='mine') and open (statusFilter='open') items. ",
    schema: {
      assigneeFilter: z
        .enum(["mine", "all"])
        .default("mine")
        .optional()
        .describe(
          "Which tasks to return. 'mine' = only the current user's tasks (default); 'all' = all tasks."
        ),
      statusFilter: z
        .enum(["open", "done", "all"])
        .default("open")
        .optional()
        .describe(
          "Which tasks to return. 'open' = todo + in_progress + in_review (default); 'done' = completed; 'all' = everything."
        ),
      daysAgo: z
        .number()
        .min(1)
        .max(90)
        .optional()
        .describe(
          "When status is 'done' or 'all', limit completed tasks to this many days back. Defaults to 7."
        ),
      meetingId: z
        .string()
        .optional()
        .describe(
          "Only tasks from this meeting (its sId, shown on each task as Meeting)."
        ),
      seriesId: z
        .string()
        .optional()
        .describe(
          "Only tasks from meetings of this recurring series (its sId, shown on each task as Series)."
        ),
      dustPod: ConfigurableToolInputSchemas[
        INTERNAL_MIME_TYPES.TOOL_INPUT.DUST_POD
      ]
        .optional()
        .describe(
          "Optional Pod to list tasks from; falls back to the conversation's Pod."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing tasks",
      done: "List tasks",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: CREATE_TASKS_TOOL_NAME,
    description:
      "Create one or more tasks in the Pod that the person asked you for in their own message to you. " +
      "Anything else is kept as a proposal for a person to accept, never created; to let someone choose among tasks you found, use propose_tasks, ask with ask_user_question, then accept_proposals. " +
      "Put the owner in proposedOwner (or userId for a Pod member the person named) and the deadline in dueOn, never in the text.",
    schema: PodTasksCreateTasksInputSchema.shape,
    stake: "never_ask",
    displayLabels: {
      running: "Creating tasks",
      done: "Create tasks",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: PROPOSE_TASKS_TOOL_NAME,
    description:
      "Propose tasks you found in a meeting, a file or a conversation, for a person to review. Proposals wait in the Pod's Proposed section and never reach the board until someone accepts them. " +
      "Give each one its proposed owner, confidence and verbatim evidence. Propose first, then ask the person with ask_user_question which to create, then call accept_proposals with their answer.",
    schema: PodTasksProposeTasksInputSchema.shape,
    stake: "never_ask",
    displayLabels: {
      running: "Proposing tasks",
      done: "Propose tasks",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: ACCEPT_PROPOSALS_TOOL_NAME,
    description:
      "Create the proposed tasks a person chose by answering your ask_user_question card, applying any changes they asked for, and dismiss the ones they declined. " +
      "It only works with the id of a card the person answered; proposals they did not rule on stay proposed.",
    schema: PodTasksAcceptProposalsInputSchema.shape,
    stake: "never_ask",
    displayLabels: {
      running: "Creating chosen tasks",
      done: "Create chosen tasks",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: UPDATE_TASKS_TOOL_NAME,
    description:
      "Update one or more existing tasks in the Pod: text, status, assignee (assigneeUserId) or due date (dueOn).",
    schema: PodTasksUpdateTasksInputSchema.shape,
    stake: "low",
    displayLabels: {
      running: "Updating tasks",
      done: "Update tasks",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: START_TASK_AGENT_TOOL_NAME,
    description:
      "Start an agent conversation to work on one of your open tasks with status 'todo'. " +
      "If already started, it reuses the existing linked conversation.",
    schema: {
      taskId: z.string().describe("The sId of the task to start working on."),
      agentName: z
        .string()
        .min(3)
        .optional()
        .describe(
          "Optional agent name. If provided, the tool searches matching agent configurations and uses the best match. Defaults to Friday."
        ),
      customMessage: z
        .string()
        .optional()
        .describe(
          "Optional additional instructions appended to the kickoff message sent to the selected agent."
        ),
      dustPod: ConfigurableToolInputSchemas[
        INTERNAL_MIME_TYPES.TOOL_INPUT.DUST_POD
      ]
        .optional()
        .describe(
          "Optional Pod to look up the task in; falls back to the conversation's Pod."
        ),
    },
    stake: "low",
    displayLabels: {
      running: "Starting task work",
      done: "Start task work",
    },
    toolCostCategory: "basic",
    freeUsage: false,
  },
] as const;

export const POD_TASKS_SERVER = {
  serverInfo: {
    name: POD_TASKS_SERVER_NAME,
    version: "1.0.0",
    description:
      "Manage Pod tasks: list, create, update, and complete action items.",
    icon: "ActionCheckCircleIcon",
    authorization: null,
    documentationUrl: null,
  },
  tools: POD_TASKS_TOOLS_METADATA,
} as const satisfies ServerMetadata;
